import { useState } from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import {
  useChecklist,
  useDeleteChecklistItem,
  useDeleteChecklistSection,
  useInstantiateChecklist,
  useReorderChecklistItems,
  useReorderChecklistSections,
} from '../api/rooms'
import type {
  ChecklistItemResponse,
  ChecklistResponse,
  ChecklistSectionResponse,
  RoomResponse,
} from '../api/types'
import { ChecklistItemForm, ChecklistSectionForm } from '../components/ChecklistForms'
import { ChecklistItemDetail } from '../components/ChecklistItemDetail'
import { ChecklistMatrix } from '../components/ChecklistMatrix'
import { ExportChecklist, SaveAsTemplate, StartAList } from '../components/ChecklistPortability'
import { ChecklistFilterBar } from '../components/ChecklistFilterBar'
import { ChecklistBulkBar } from '../components/ChecklistBulkBar'
import {
  NO_FILTERS,
  applyChecklistFilters,
  filtersFromSearch,
  filtersToSearch,
  formatDateOnly,
  hasFilters,
  type ChecklistFilters,
} from '../lib/checklistView'
import { DataPolicyGate } from '../components/DataPolicyGate'
import { dataPolicyBlocks } from '../lib/dataPolicy'
import {
  canEditChecklist,
  checklistCounts,
  checklistStatusLabel,
  checklistSummary,
  groupChecklist,
  type ChecklistSectionGroup,
} from '../lib/checklist'
import { errorMessage } from '../lib/format'

/**
 * The Master DRL's workstreams, for the dialog that builds a list from it. The template
 * itself lives in the API (ADR 0007 s7) and has no read route until Phase 3's template
 * tables; these six lines are the one thing the screen needs to know about it, and
 * `MasterDrlTests` pins the same codes and counts on the server.
 */
export const MASTER_DRL_WORKSTREAMS = [
  { code: 'FIN', name: 'Financial', count: 21 },
  { code: 'LEG', name: 'Legal', count: 43 },
  { code: 'TAX', name: 'Tax', count: 15 },
  { code: 'INS', name: 'Insurance', count: 13 },
  { code: 'EEB', name: 'Employee Benefits', count: 18 },
  { code: 'BUS', name: 'Business', count: 9 },
] as const

/**
 * The room's request list (ADR 0006, extended by ADR 0007): sections (the workbook's
 * workstreams), categories inside them, each request with its stable label, priority and state.
 * Owners shape it, or build it from the Master DRL; responders work it; owners sign it off,
 * after the freeze too. Every control is drawn from what the server said the caller may do,
 * and the API decides again on the click.
 */
export function Checklist() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const checklist = useChecklist(roomId)
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState<
    'item' | 'section' | 'template' | 'portable' | 'save-template' | null
  >(null)
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<ChecklistItemResponse | null>(null)
  const [editingSection, setEditingSection] = useState<ChecklistSectionResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The filters live in the URL and nowhere else (ADR 0009): a narrowed list can be pasted into an email,
  // and nothing is kept in the browser. This is the SPA's first use of the query string.
  const [params, setParams] = useSearchParams()
  const filters = filtersFromSearch(params)
  const setFilters = (next: ChecklistFilters) => setParams(filtersToSearch(next), { replace: true })
  const [ticked, setTicked] = useState<string[]>([])
  const removeItem = useDeleteChecklistItem(roomId)
  const reorderItems = useReorderChecklistItems(roomId)
  const reorderSections = useReorderChecklistSections(roomId)

  if (checklist.isPending) return <p className="muted small">Loading checklist…</p>
  if (checklist.isError) return <p className="error-text small">{errorMessage(checklist.error)}</p>
  const data = checklist.data
  const filtered = hasFilters(filters)
  const shown = applyChecklistFilters(data.items, filters)
  // Structure (add, edit, move, remove) is shaped on the whole list: a reorder sent from a narrowed view
  // would name only some of a section's items, and the API refuses that. Ticking and the bulk edit work narrowed.
  const canShape = canEditChecklist(data, room.status)
  const editable = canShape && !filtered
  const groups = groupChecklist(data.sections, shown).filter((g) => !filtered || g.items.length > 0)
  const tickedShown = ticked.filter((id) => shown.some((i) => i.id === id))
  const toggleTick = (id: string) =>
    setTicked((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]))
  const fail = (e: unknown) => setError(errorMessage(e))
  const canBuild =
    editable && !data.sections.some((s) => MASTER_DRL_WORKSTREAMS.some((w) => w.code === s.code))

  const closeForms = () => {
    setAdding(null)
    setAddingTo(null)
    setEditingItem(null)
    setEditingSection(null)
  }

  /** Swaps an item with its neighbour inside its section and sends the section's whole new order. */
  const nudgeItem = (
    group: ChecklistSectionGroup<ChecklistItemResponse>,
    index: number,
    by: -1 | 1,
  ) => {
    const ids = group.items.map((i) => i.id)
    const other = index + by
    const a = ids[index]
    const b = ids[other]
    if (a === undefined || b === undefined) return
    ids[index] = b
    ids[other] = a
    setError(null)
    reorderItems.mutate({ sectionId: group.section?.id ?? null, itemIds: ids }, { onError: fail })
  }

  const nudgeSection = (index: number, by: -1 | 1) => {
    const ids = groups.flatMap((g) => (g.section ? [g.section.id] : []))
    const other = index + by
    const a = ids[index]
    const b = ids[other]
    if (a === undefined || b === undefined) return
    ids[index] = b
    ids[other] = a
    setError(null)
    reorderSections.mutate(ids, { onError: fail })
  }

  return (
    <div className="stack">
      {/* ADR 0008: a responder's move is information handed over, and the API refuses it until
          the organization's data policy is accepted. Asked here, once, rather than discovered
          as a refusal on the first item they try to answer. */}
      {data.canSubmit && dataPolicyBlocks(room) && (
        <DataPolicyGate room={room} doing="you answer a request in this room" />
      )}
      <ChecklistMatrix
        sections={data.sections}
        items={data.items}
        onPickSection={(section) => setFilters({ ...NO_FILTERS, section })}
      />
      {data.items.length > 0 && (
        <ChecklistFilterBar
          filters={filters}
          onChange={setFilters}
          sections={data.sections}
          items={data.items}
          shown={shown.length}
          canManage={data.canManage}
        />
      )}
      {canShape && (
        <ChecklistBulkBar
          roomId={roomId}
          selected={tickedShown}
          sections={data.sections}
          onDone={() => setTicked([])}
        />
      )}
      <section className="panel">
        <div className="row-between">
          <div>
            <h2 className="h2 no-margin">Checklist</h2>
            <p className="small muted">{checklistSummary(data.items)}</p>
            {data.items.length > 0 && adding === null && (
              <div className="row-actions">
                <ExportChecklist roomId={roomId} />
                {data.canManage && data.sections.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setAdding('save-template')}
                  >
                    Save as a template
                  </button>
                )}
              </div>
            )}
          </div>
          {editable && adding === null && !editingItem && !editingSection && (
            <div className="row-actions">
              {canBuild && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setAdding('template')}
                >
                  Start from the Master DRL
                </button>
              )}
              <button type="button" className="btn btn-sm" onClick={() => setAdding('portable')}>
                Templates, copy or import
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setAdding('section')}>
                Add a section
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setAdding('item')}>
                Add an item
              </button>
            </div>
          )}
        </div>
        {canShape && filtered && (
          <p className="small muted">
            The list is narrowed. Tick requests to edit them together; clear the filters to add,
            move or remove.
          </p>
        )}
        {filtered && shown.length === 0 && (
          <p className="muted small">No request matches these filters.</p>
        )}
        {error && <p className="error-text small">{error}</p>}
        {adding === 'template' && <BuildFromMasterDrl roomId={roomId} onDone={closeForms} />}
        {adding === 'portable' && (
          <StartAList
            roomId={roomId}
            organizationId={room.organizationId}
            livingSectionCodes={data.sections.map((s) => s.code)}
            onDone={closeForms}
          />
        )}
        {adding === 'save-template' && (
          <SaveAsTemplate
            roomId={roomId}
            organizationId={room.organizationId}
            onDone={closeForms}
          />
        )}
        {adding === 'section' && <ChecklistSectionForm roomId={roomId} onDone={closeForms} />}
        {adding === 'item' && (
          <ChecklistItemForm
            roomId={roomId}
            sections={data.sections}
            items={data.items}
            defaultSectionId={addingTo}
            onDone={closeForms}
          />
        )}
        {editingSection && (
          <ChecklistSectionForm roomId={roomId} section={editingSection} onDone={closeForms} />
        )}
        {editingItem && (
          <ChecklistItemForm
            roomId={roomId}
            sections={data.sections}
            items={data.items}
            item={editingItem}
            onDone={closeForms}
          />
        )}
        {groups.length === 0 && adding === null && (
          <p className="muted small">
            {data.canManage
              ? 'Nothing is expected yet. Build the list from the Master DRL, or add the documents this room should receive.'
              : 'The owner has not listed what this room should receive.'}
          </p>
        )}
      </section>

      {groups.map((group, index) => (
        <SectionBlock
          key={group.section?.id ?? 'uncategorized'}
          roomId={roomId}
          room={room}
          group={group}
          data={data}
          editable={editable}
          selectable={canShape}
          ticked={ticked}
          onTick={toggleTick}
          onTickAll={(ids, on) =>
            setTicked((now) =>
              on ? [...new Set([...now, ...ids])] : now.filter((id) => !ids.includes(id)),
            )
          }
          open={open}
          onOpen={(id) => setOpen(open === id ? null : id)}
          onOpenRelated={(id) => {
            // A related request may be outside a narrowed view: widen it rather than open nothing.
            if (!shown.some((i) => i.id === id)) setFilters(NO_FILTERS)
            setOpen(id)
          }}
          onAddItem={() => {
            closeForms()
            setAddingTo(group.section?.id ?? null)
            setAdding('item')
          }}
          onEditSection={() => {
            closeForms()
            setEditingSection(group.section)
          }}
          onEditItem={(item) => {
            closeForms()
            setEditingItem(item)
          }}
          onRemoveItem={(item) => {
            if (
              window.confirm(
                `Remove "${item.title}" from the checklist? Documents keep their files; only the link goes.${
                  item.docLabel === null ? '' : ` Its label, ${item.docLabel}, is never used again.`
                }`,
              )
            ) {
              setError(null)
              removeItem.mutate(item.id, { onError: fail })
            }
          }}
          onNudgeItem={(i, by) => nudgeItem(group, i, by)}
          onNudgeSection={group.section ? (by) => nudgeSection(index, by) : undefined}
          isFirstSection={index === 0}
          isLastSection={index === groups.filter((g) => g.section).length - 1}
          onError={fail}
        />
      ))}
    </div>
  )
}

function SectionBlock({
  roomId,
  room,
  group,
  data,
  editable,
  selectable,
  ticked,
  onTick,
  onTickAll,
  open,
  onOpen,
  onOpenRelated,
  onAddItem,
  onEditSection,
  onEditItem,
  onRemoveItem,
  onNudgeItem,
  onNudgeSection,
  isFirstSection,
  isLastSection,
  onError,
}: {
  roomId: string
  room: RoomResponse
  group: ChecklistSectionGroup<ChecklistItemResponse>
  data: ChecklistResponse
  editable: boolean
  /** Rows can be ticked for the bulk edit: an owner, in a Draft or Open room, narrowed or not. */
  selectable: boolean
  ticked: string[]
  onTick: (itemId: string) => void
  onTickAll: (itemIds: string[], on: boolean) => void
  open: string | null
  onOpen: (itemId: string) => void
  onOpenRelated: (itemId: string) => void
  onAddItem: () => void
  onEditSection: () => void
  onEditItem: (item: ChecklistItemResponse) => void
  onRemoveItem: (item: ChecklistItemResponse) => void
  onNudgeItem: (index: number, by: -1 | 1) => void
  onNudgeSection?: (by: -1 | 1) => void
  isFirstSection: boolean
  isLastSection: boolean
  onError: (e: unknown) => void
}) {
  const removeSection = useDeleteChecklistSection(roomId)
  const [reassignTo, setReassignTo] = useState('')
  const [removing, setRemoving] = useState(false)
  const { section } = group
  const counts = checklistCounts(group.items)
  const others = data.sections.filter((s) => s.id !== section?.id)

  return (
    // A native <details>: collapsing 119 requests needs no state and no script.
    <details className="panel checklist-section" open>
      <summary className="checklist-section-summary">
        <span className="checklist-section-name">
          {section && <span className="mono muted">{section.code} </span>}
          {group.name}
        </span>
        <span className="small muted">
          {counts.done + counts.notApplicable} of {counts.total} answered
          {counts.awaitingSignOff > 0 && ` · ${counts.awaitingSignOff} awaiting sign-off`}
          {counts.sentBack > 0 && ` · ${counts.sentBack} sent back`}
          {section?.serviceProvider && ` · ${section.serviceProvider}`}
          {/* The linked folder's live index where there is one the caller may see; else the workbook's hint. */}
          {section?.vdrIndex
            ? ` · folder ${section.vdrIndex} ${section.vdrFolderName ?? ''}`
            : section?.vdrIndexHint && ` · folder ${section.vdrIndexHint}`}
        </span>
      </summary>

      {section?.description && <p className="small muted">{section.description}</p>}
      {!section && (
        <p className="small muted">
          Items that are in no section. They have no document label; move one into a section to give
          it one.
        </p>
      )}

      {editable && (
        <div className="row-actions">
          <button type="button" className="btn btn-sm" onClick={onAddItem}>
            Add an item here
          </button>
          {section && (
            <>
              <button type="button" className="btn btn-sm" onClick={onEditSection}>
                Edit section
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={isFirstSection}
                onClick={() => onNudgeSection?.(-1)}
              >
                Move up
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={isLastSection}
                onClick={() => onNudgeSection?.(1)}
              >
                Move down
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => setRemoving(!removing)}
              >
                Remove section
              </button>
            </>
          )}
        </div>
      )}

      {removing && section && (
        <form
          className="inline-form"
          aria-label={`Remove section ${section.name}`}
          onSubmit={(e) => {
            e.preventDefault()
            removeSection.mutate(
              { sectionId: section.id, reassignTo: reassignTo || undefined },
              { onSuccess: () => setRemoving(false), onError },
            )
          }}
        >
          {group.items.length > 0 ? (
            <label>
              Its {group.items.length} items go to{' '}
              <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                <option value="">Choose a section</option>
                {others.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="small">This section is empty.</span>
          )}
          <button
            type="submit"
            className="btn btn-sm btn-danger"
            disabled={removeSection.isPending || (group.items.length > 0 && !reassignTo)}
          >
            Remove
          </button>
          <span className="small muted">
            Items keep their labels. The section's prefix is never used again.
          </span>
        </form>
      )}

      {group.items.length === 0 && <p className="muted small">No items in this section yet.</p>}

      {group.categories.map((category) => (
        <div key={category.category} className="checklist-category">
          {(group.categories.length > 1 || category.category !== 'General') && (
            <h3 className="h4 checklist-category-name">{category.category}</h3>
          )}
          <table className="table">
            <thead>
              <tr>
                {selectable && (
                  <th>
                    <input
                      type="checkbox"
                      aria-label={`Tick every request under ${category.category}`}
                      checked={category.items.every((i) => ticked.includes(i.id))}
                      onChange={(e) =>
                        onTickAll(
                          category.items.map((i) => i.id),
                          e.target.checked,
                        )
                      }
                    />
                  </th>
                )}
                <th>Label</th>
                <th>Item</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Goal date</th>
                <th>Documents</th>
                {editable && <th></th>}
              </tr>
            </thead>
            <tbody>
              {category.items.map((item) => {
                const index = group.items.indexOf(item)
                const isOpen = open === item.id
                return (
                  <ItemRows
                    key={item.id}
                    roomId={roomId}
                    room={room}
                    item={item}
                    data={data}
                    section={group.section}
                    editable={editable}
                    selectable={selectable}
                    ticked={ticked.includes(item.id)}
                    onTick={() => onTick(item.id)}
                    onOpenRelated={onOpenRelated}
                    isOpen={isOpen}
                    onOpen={() => onOpen(item.id)}
                    onEdit={() => onEditItem(item)}
                    onRemove={() => onRemoveItem(item)}
                    onUp={index > 0 ? () => onNudgeItem(index, -1) : undefined}
                    onDown={
                      index < group.items.length - 1 ? () => onNudgeItem(index, 1) : undefined
                    }
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      ))}
    </details>
  )
}

function ItemRows({
  roomId,
  room,
  item,
  data,
  section,
  editable,
  selectable,
  ticked,
  onTick,
  onOpenRelated,
  isOpen,
  onOpen,
  onEdit,
  onRemove,
  onUp,
  onDown,
}: {
  roomId: string
  room: RoomResponse
  item: ChecklistItemResponse
  data: ChecklistResponse
  section: ChecklistSectionResponse | null
  editable: boolean
  selectable: boolean
  ticked: boolean
  onTick: () => void
  onOpenRelated: (itemId: string) => void
  isOpen: boolean
  onOpen: () => void
  onEdit: () => void
  onRemove: () => void
  onUp?: () => void
  onDown?: () => void
}) {
  return (
    <>
      <tr className={isOpen ? 'row-selected' : undefined}>
        {selectable && (
          <td>
            <input
              type="checkbox"
              checked={ticked}
              onChange={onTick}
              aria-label={`Tick ${item.title}`}
            />
          </td>
        )}
        <td className="mono">{item.docLabel ?? '—'}</td>
        <td>
          <button type="button" className="link-btn" aria-expanded={isOpen} onClick={onOpen}>
            {item.title}
          </button>
          {item.assignedToMe && <span className="chip chip-mine"> yours</span>}
        </td>
        <td>
          <ChecklistStatusChip status={item.status} />
        </td>
        <td>
          {item.priority && (
            <span className={`chip priority-${item.priority.toLowerCase()}`}>{item.priority}</span>
          )}
        </td>
        <td className={item.isOverdue ? 'small overdue' : 'small'}>
          {formatDateOnly(item.goalDate)}
          {item.isOverdue && ' · overdue'}
        </td>
        <td className="mono">{item.documentCount}</td>
        {editable && (
          <td className="td-actions">
            <button
              type="button"
              className="btn btn-sm"
              disabled={!onUp}
              onClick={onUp}
              aria-label={`Move ${item.title} up`}
            >
              ↑
            </button>{' '}
            <button
              type="button"
              className="btn btn-sm"
              disabled={!onDown}
              onClick={onDown}
              aria-label={`Move ${item.title} down`}
            >
              ↓
            </button>{' '}
            <button type="button" className="btn btn-sm" onClick={onEdit}>
              Edit
            </button>{' '}
            <button type="button" className="btn btn-sm btn-danger" onClick={onRemove}>
              Remove
            </button>
          </td>
        )}
      </tr>
      {isOpen && (
        <tr className="row-detail">
          <td colSpan={6 + (editable ? 1 : 0) + (selectable ? 1 : 0)}>
            <ChecklistItemDetail
              roomId={roomId}
              roomStatus={room.status}
              item={item}
              checklist={data}
              room={room}
              section={section}
              onOpenRelated={onOpenRelated}
            />
          </td>
        </tr>
      )}
    </>
  )
}

export function ChecklistStatusChip({ status }: { status: string }) {
  return (
    <span className={`chip status-${status.toLowerCase()}`}>{checklistStatusLabel(status)}</span>
  )
}

/** Builds the room's list from the Master DRL in one call; the owner chooses which workstreams. */
function BuildFromMasterDrl({ roomId, onDone }: { roomId: string; onDone: () => void }) {
  const instantiate = useInstantiateChecklist(roomId)
  const [codes, setCodes] = useState<string[]>(MASTER_DRL_WORKSTREAMS.map((w) => w.code))
  const total = MASTER_DRL_WORKSTREAMS.filter((w) => codes.includes(w.code)).reduce(
    (n, w) => n + w.count,
    0,
  )

  return (
    <form
      className="form form-wide"
      aria-label="Start from the Master DRL"
      onSubmit={(e) => {
        e.preventDefault()
        instantiate.mutate(
          {
            template: 'master-drl',
            sectionCodes: codes.length === MASTER_DRL_WORKSTREAMS.length ? null : codes,
          },
          { onSuccess: onDone },
        )
      }}
    >
      <p className="no-margin">
        The Master Due Diligence Request List: six workstreams, 119 requests, each with its document
        label (1001 to 6009), category, priority and the request in full. It is copied into this
        room; changing it here changes nothing anywhere else, and items already on this list are
        left alone.
      </p>
      <fieldset className="checklist-picker">
        <legend className="small">Workstreams to include</legend>
        <div className="chips">
          {MASTER_DRL_WORKSTREAMS.map((w) => {
            const picked = codes.includes(w.code)
            return (
              <label
                key={w.code}
                className={picked ? 'chip chip-pick chip-picked' : 'chip chip-pick'}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  onChange={() =>
                    setCodes(picked ? codes.filter((c) => c !== w.code) : [...codes, w.code])
                  }
                />
                {w.name} ({w.count})
              </label>
            )
          })}
        </div>
      </fieldset>
      <p className="small muted no-margin">
        The financial requests still name the period of the deal they were written for. Read them,
        and edit the dates, before the list goes to anyone.
      </p>
      {instantiate.error && <p className="error-text small">{errorMessage(instantiate.error)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={instantiate.isPending || codes.length === 0}
        >
          {instantiate.isPending ? 'Building…' : `Add ${total} requests`}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
